import { useMemo } from "react";

import { PickAndMixQuery } from "@chef/state-management";
import { useViewport } from "@chef/hooks";
import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import { nonNullable } from "@chef/utils/nonNullable";
import { isEqualStrings } from "@chef/utils/equal";
import { Skeleton } from "@chef/components";

import type { WeekYear } from "@chef/types/common";

import { MyMenuCard, MyMenuCardProps } from "./MyMenuCard";
import { WelcomeBackSnackbar } from "../WelcomeBackSnackbar";
import { MyMenuCardData, useMyMenuCardsData } from "../../hooks";

interface MyMenuCardsProps extends WeekYear {
  recipeCards: MyMenuCardData[];
  pickAndMix: PickAndMixQuery["pickAndMix"];
  deliveryStatus: MyMenuCardProps["deliveryStatus"];
  portions: number;
  handleRemoveRecipe: (productId: string) => void;
}

const pickRecipeImageFromPickAndMix = (
  pickAndMix: PickAndMixQuery["pickAndMix"],
  n: number,
) => {
  const images = pickAndMix
    .flatMap((p) => p.recipes)
    .map((r) => findFeaturedPortraitImage(r)?.url)
    .filter(nonNullable);

  return images[n % images.length];
};

const MAX_CARD_COUNT = 5;

const MyMenuCardsMobile = ({
  week,
  year,
  recipeCards,
  pickAndMix,
  deliveryStatus,
  portions,
  handleRemoveRecipe,
}: MyMenuCardsProps) => {
  const cards: MyMenuCardData[] = useMemo(() => {
    const selectedCount = recipeCards.length;

    if (selectedCount === 0) {
      return [
        {
          key: "upsale-0",
          type: "upsale",
          img: pickRecipeImageFromPickAndMix(pickAndMix, 0),
        },
      ];
    }

    if (selectedCount < MAX_CARD_COUNT) {
      return [
        ...recipeCards,
        {
          key: `upsale-${selectedCount}`,
          type: "upsale",
          img: pickRecipeImageFromPickAndMix(pickAndMix, selectedCount),
        },
      ];
    }

    return recipeCards;
  }, [recipeCards, pickAndMix]);

  return (
    <div className="overflow-hidden">
      <div className="relative flex items-stretch px-4 space-x-2 overflow-x-scroll scrollbar-hidden snap-x md:space-x-4">
        {cards.map((card) => (
          <div
            key={card.key}
            className="flex flex-shrink-0 pb-1 w-52 snap-start"
          >
            {card.type === "recipe" ? (
              <MyMenuCard
                type="recipe"
                recipe={card.recipe}
                week={week}
                year={year}
                deliveryStatus={deliveryStatus}
                portions={portions.toString()}
                handleRemoveRecipe={() => handleRemoveRecipe(card.productId)}
              />
            ) : (
              <MyMenuCard
                type="upsale"
                img={card.img}
                week={week}
                year={year}
                deliveryStatus={deliveryStatus}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const MyMenuCardsDesktop = ({
  week,
  year,
  recipeCards,
  pickAndMix,
  deliveryStatus,
  portions,
  handleRemoveRecipe,
}: MyMenuCardsProps) => {
  const selectedCount = recipeCards.length;

  const upsaleCount = Math.max(0, MAX_CARD_COUNT - selectedCount);
  const upsaleCards: MyMenuCardData[] = [...Array(upsaleCount)].map((_, i) => ({
    key: `upsale-${i}`,
    type: "upsale",
    img: pickRecipeImageFromPickAndMix(pickAndMix, selectedCount + i),
  }));

  const cards = [...recipeCards, ...upsaleCards];

  return (
    <section className="relative grid grid-cols-2 gap-4 lg:grid-cols-5">
      {cards.map((card) =>
        card.type === "recipe" ? (
          <MyMenuCard
            key={card.key}
            type="recipe"
            recipe={card.recipe}
            week={week}
            year={year}
            deliveryStatus={deliveryStatus}
            portions={portions.toString()}
            handleRemoveRecipe={() => handleRemoveRecipe(card.productId)}
          />
        ) : (
          <MyMenuCard
            key={card.key}
            type="upsale"
            img={card.img}
            week={week}
            year={year}
            deliveryStatus={deliveryStatus}
          />
        ),
      )}
    </section>
  );
};

export const MyMenuCards = ({ week, year }: WeekYear) => {
  const [breakpoint] = useViewport();
  const mobileView = breakpoint === "sm";
  const {
    meals,
    portions,
    recipeCards,
    pickAndMix,
    deliveryStatus,
    isOrderGenerated,
    handleRemoveRecipe,
    isSelectedProductsReady,
  } = useMyMenuCardsData({ week, year });

  if (!isOrderGenerated) {
    return (
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-center w-full h-full pt-10 place-items-start lg:pt-0 lg:place-items-center bg-background/70">
        <WelcomeBackSnackbar
          isPausedAccount={isEqualStrings(deliveryStatus, "freezed")}
        />
      </div>
    );
  }

  if (!meals || !portions || !pickAndMix) {
    return null;
  }

  const commonProps = {
    week,
    year,
    recipeCards,
    pickAndMix,
    deliveryStatus,
    portions,
    handleRemoveRecipe,
  };

  if (!isSelectedProductsReady) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Skeleton height={80} />
        <Skeleton height={80} />
        <Skeleton height={80} />
        <Skeleton height={80} />
        <Skeleton height={80} />
      </div>
    );
  }

  if (mobileView) {
    return <MyMenuCardsMobile {...commonProps} />;
  } else {
    return <MyMenuCardsDesktop {...commonProps} />;
  }
};
